/**
 * Promotional access — effect primitives (promo plan Phase P1).
 *
 * A **promo effect** is a typed, self-describing unit of "what is granted". It
 * is the modularity primitive of the promo system: a new promotion kind is a
 * new effect variant + one combinator branch in the entitlement resolver, and
 * nothing else changes.
 *
 * Browser-safe by construction (zod only, no DB / Node): the SPA renders
 * effects and the API validates them from the SAME schema, so the two can
 * never fork. `@papan/database`'s schema redeclares a structural `PromoEffect`
 * for its jsonb `$type<>` annotation (it keeps no dependency on this package) —
 * the two shapes are kept identical by hand, exactly like `BudgetCap` /
 * `BillingPlanFeatures`.
 *
 * Plan: docs/plans/2026-07-22_PROMO_ACCESS_MODULAR_BILLING.md (Revision 1)
 *
 * ── Hard rules encoded here ──
 * - `credit` (Revision 1, replaces `budget_boost`) is a DRAWABLE POOL consumed
 *   at the head of the funding waterfall — never a cap raise. There is
 *   deliberately NO effect that touches the billable *extra* axis: raising
 *   overage would increase what a customer can be charged, the opposite of a
 *   promotion.
 * - All magnitudes are non-negative — a promo never subtracts.
 * - Every numeric magnitude carries an explicit upper bound. Without one a
 *   fat-fingered (or compromised) admin token could mint an astronomically
 *   large drawable pool; and any value above 2^53 (`Number.MAX_SAFE_INTEGER`)
 *   loses precision the moment downstream code does `Number(...)` on the stored
 *   jsonb. Each ceiling below is a defensible operational maximum kept well
 *   under 2^53 — a legitimate promo never approaches it.
 */
import { z } from 'zod'

/**
 * Effect-magnitude ceilings. Deliberately generous (no real promo hits them),
 * but every one is far below 2^53 so the values survive `Number()` coercion.
 */
/**
 * One million MAJOR units of the billing currency, in billing micro.
 *
 * Was `CREDIT_AMOUNT_BILLING_MICRO_MAX` — same magnitude, different unit, and the
 * rename is the point: a promo grant is now denominated in what the deployment
 * charges in, so a ceiling named for dollars would be describing a quantity
 * that no longer exists.
 */
export const CREDIT_AMOUNT_BILLING_MICRO_MAX = 1_000_000_000_000
/** ~4 PB — orders of magnitude above any real storage grant, well under 2^53. */
export const STORAGE_BOOST_BYTES_MAX = 4_000_000_000_000_000
/** Concurrency slots — no tenant runs a four-digit fleet of agents. */
export const CONCURRENCY_BOOST_SLOTS_MAX = 1_000

/** Promote a tenant to a plan's entitlements without a subscription row.
 *  `planId` references `billing_plans.id`; that the plan EXISTS is checked at
 *  write time by the route (not here — zod only shape-validates). */
export const PlanOverrideEffectSchema = z.object({
  kind: z.literal('plan_override'),
  planId: z.string().min(1),
})

/**
 * Drawable promotional credit, in BILLING-CURRENCY MICRO units (Revision 1 —
 * replaces the cap-raise `budget_boost`; re-denominated by P8/P9).
 *
 * Consumed FIRST in the funding waterfall (`promo → free → …`); the per-grant
 * draw-down accumulator lives on `promo_grants.credit_consumed_billing_micro`,
 * and unused credit simply stops being offered when the grant's window ends.
 * Positive by construction — a zero credit is a meaningless grant, not a block.
 *
 * ── WHY THIS FLIPPED WITH THE PREPAID POOL, NOT AFTER IT ────────────────
 *
 * A promo grant is spent against the SAME usage rows the prepaid balance is,
 * through the same waterfall, in the same transaction. Leaving it in USD would
 * have meant one drawable pool debited in billing micro and its neighbour in
 * USD, kept apart only by the fact that the eligibility test happens to be
 * `> 0` and therefore unit-blind. That is not a design; it is two units one
 * refactor away from being added together — and the arithmetic that would mix
 * them produces a plausible number and no error.
 *
 * `billingCurrency` rides on the effect so a grant minted before a
 * re-denomination cannot be consumed against a pool in another currency.
 */
export const CreditEffectSchema = z.object({
  kind: z.literal('credit'),
  amountBillingMicro: z
    .number()
    .int()
    .positive()
    .max(
      CREDIT_AMOUNT_BILLING_MICRO_MAX,
      'credit amountBillingMicro exceeds the 1,000,000 major-unit maximum',
    ),
  /**
   * The unit `amountBillingMicro` is denominated in — lowercase ISO-4217.
   *
   * ── REQUIRED, AND THAT IS THE WHOLE GUARANTEE ──────────────────────
   *
   * It was optional in the first cut, with absence meaning "the deployment's
   * own". That reading is exactly the bug the field exists to prevent: an
   * unstamped grant is one whose currency is UNKNOWN, and resolving unknown to
   * "whatever we charge in today" is how a US$50 grant becomes an RM50 one at
   * no exchange rate at all. The permissive default made every grant minted
   * before the flip silently spendable against a pool it was never denominated
   * in.
   *
   * There is no legacy row to be lenient for. 0177 REFUSES to migrate a
   * database with any surviving `promo_grants` / `promo_codes` /
   * `promo_redemptions` row, so every grant that can exist after the window was
   * minted by code that stamps this. Requiring it turns "unstamped" from a
   * silently-permitted state into a parse error at the boundary.
   */
  billingCurrency: z.string().length(3).toLowerCase(),
})

/** Add storage headroom, in bytes. */
export const StorageBoostEffectSchema = z.object({
  kind: z.literal('storage_boost'),
  bytes: z
    .number()
    .int()
    .nonnegative()
    .max(STORAGE_BOOST_BYTES_MAX, 'storage_boost bytes exceeds the ~4 PB maximum'),
})

/** Add agent concurrency slots. */
export const ConcurrencyBoostEffectSchema = z.object({
  kind: z.literal('concurrency_boost'),
  slots: z
    .number()
    .int()
    .nonnegative()
    .max(CONCURRENCY_BOOST_SLOTS_MAX, 'concurrency_boost slots exceeds the 1,000 maximum'),
})

/** Unlock named feature flags (set-unioned into `tenant` features). */
export const FeatureUnlockEffectSchema = z.object({
  kind: z.literal('feature_unlock'),
  features: z.array(z.string().min(1)).min(1),
})

/**
 * The discriminated union validated on every admin/code write. Stored rows are
 * never trusted on their own — the resolver additionally ignores unknown kinds
 * (defense in depth), so an effect kind retired in a future version degrades to
 * a no-op rather than throwing at enforcement time.
 */
export const PromoEffectSchema = z.discriminatedUnion('kind', [
  PlanOverrideEffectSchema,
  CreditEffectSchema,
  StorageBoostEffectSchema,
  ConcurrencyBoostEffectSchema,
  FeatureUnlockEffectSchema,
])

export type PromoEffect = z.infer<typeof PromoEffectSchema>

/** A grant/code carries at least one effect — an empty promo is meaningless. */
export const PromoEffectsSchema = z.array(PromoEffectSchema).min(1)

/**
 * What a CLIENT may send — the same union with `billingCurrency` absent.
 *
 * The currency of a promo grant is a fact about the DEPLOYMENT, not an operator
 * input: it is whichever currency the tenant is billed in, resolved server-side
 * at mint. So the wire shape does not carry it at all, and a client that sends
 * one is ignored rather than trusted — an operator cannot mint a USD grant on
 * an MYR deployment by editing a request body.
 *
 * The stored shape (`PromoEffectSchema`) REQUIRES it. `validateEffects` parses
 * with this, stamps, and re-parses with the strict one, so anything that leaves
 * the boundary satisfies the contract every consumer relies on.
 */
export const PromoEffectInputSchema = z.discriminatedUnion('kind', [
  PlanOverrideEffectSchema,
  CreditEffectSchema.omit({ billingCurrency: true }),
  StorageBoostEffectSchema,
  ConcurrencyBoostEffectSchema,
  FeatureUnlockEffectSchema,
])

export const PromoEffectsInputSchema = z.array(PromoEffectInputSchema).min(1)

/** What a client builds and sends — the stored shape minus the server's stamp. */
export type PromoEffectInput = z.infer<typeof PromoEffectInputSchema>

export type PromoEffectKind = PromoEffect['kind']
